import { ArrowRight, type LucideIcon } from 'lucide-react';

export interface PageGuideAction {
  icon: LucideIcon;
  text: string;
}

export interface PageGuideCardProps {
  /** Small-caps eyebrow, e.g. "Opportunities" or "ROI Model". */
  label: string;
  /** One sentence on what this context is for. */
  lead: string;
  /** Section level: render `lead` as a left-bordered inset quoting the section's popover. */
  quoteLead?: boolean;
  /** Exactly three. */
  actions?: PageGuideAction[];
  /** The top FAQ for this context, rendered as the one button in the card. */
  topQuestion?: { id: string; question: string } | null;
  onAsk: (faqId: string) => void;
}

/** What the chat pane opens onto, so it is never empty. */
export function PageGuideCard({ label, lead, quoteLead = false, actions = [], topQuestion = null, onAsk }: PageGuideCardProps) {
  return (
    <div className="flex flex-col gap-3.5 rounded-[10px] border border-[#e5e7eb] bg-white p-[18px]">
      <div className="flex flex-col gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-dvaNavy">{label}</span>
        {lead && (
          <span className={['text-sm leading-[1.55] text-dvaNavy', quoteLead ? 'border-l-2 border-dvaNavy pl-[13px]' : ''].join(' ')}>
            {lead}
          </span>
        )}
      </div>

      {actions.length > 0 && (
        <div className="flex flex-col gap-[9px]">
          <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-[#717182]">What you can do here</span>
          {actions.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-start gap-2.5">
              <span className="mt-px flex size-5 shrink-0 items-center justify-center rounded-md bg-dvaNavy/[0.06]">
                <Icon className="size-3 text-dvaNavy" strokeWidth={2.2} aria-hidden="true" />
              </span>
              <span className="grow text-[13px] leading-[1.5] text-dvaNavy">{text}</span>
            </div>
          ))}
        </div>
      )}

      {topQuestion && (
        <>
          <span className="block h-px bg-[#e5e7eb]" />
          <div className="flex flex-col gap-[9px]">
            <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-[#717182]">Most asked here</span>
            <button
              type="button"
              onClick={() => onAsk(topQuestion.id)}
              className="flex w-full items-center gap-3 rounded-lg border-[1.5px] border-dvaNavy bg-white px-3.5 py-[11px] text-left hover:bg-dvaNavy/[0.04] focus-visible:ring-[3px] focus-visible:ring-dvaOrange/35 focus-visible:outline-none"
            >
              <span className="grow text-[13.5px] font-semibold leading-[1.4] text-dvaNavy">{topQuestion.question}</span>
              <ArrowRight className="size-4 shrink-0 text-dvaNavy" strokeWidth={2.2} aria-hidden="true" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
